/**
 * MHR Criteria Editor Page
 *
 * Manage inclusion and exclusion criteria for a protocol.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Sparkles, AlertCircle, CheckCircle, XCircle, FileText } from 'lucide-react';
import { useMHR } from '../contexts';
import { CriterionCard, CriterionForm, TemplatePickerModal } from '../components/CriteriaForm';
import { ProtocolStatusBadge } from '../components/protocol/ProtocolStatusBadge';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { Breadcrumb } from '../components/Breadcrumb';
import { Toast } from '../components/Toast';
import * as mhrApi from '../lib/api';
import type { Protocol, Criterion, CriterionFormData, CriterionType, StructuredDefinition, CriteriaTemplate, CriteriaTemplatesByCategory, CriterionCategory, CATEGORY_LABELS } from '../types';

const CATEGORY_LABELS_MAP: Record<string, string> = {
  demographics: 'Demographics',
  medical_history: 'Medical History',
  medications: 'Medications',
  laboratory: 'Laboratory',
  procedures: 'Procedures',
  lifestyle: 'Lifestyle',
  reproductive: 'Reproductive',
  consent: 'Consent',
  other: 'Other',
};

export function CriteriaEditor() {
  const { protocolId } = useParams<{ protocolId: string }>();
  const navigate = useNavigate();
  const { refreshProtocol } = useMHR();

  // State
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<Criterion | null>(null);
  const [formType, setFormType] = useState<CriterionType>('inclusion');

  // Batch generate state
  const [generatingAll, setGeneratingAll] = useState(false);

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragType, setDragType] = useState<CriterionType | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  // Template picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatePickerType, setTemplatePickerType] = useState<CriterionType>('inclusion');
  const [templates, setTemplates] = useState<CriteriaTemplatesByCategory | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // Pre-filled form data from template
  const [templateFormData, setTemplateFormData] = useState<Partial<CriterionFormData> | null>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; criterion: Criterion | null }>({
    open: false,
    criterion: null,
  });
  const [deleting, setDeleting] = useState(false);

  // Batch generate confirmation modal state
  const [showBatchGenerateModal, setShowBatchGenerateModal] = useState(false);

  // Load data
  useEffect(() => {
    if (!protocolId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [protocolData, criteriaData] = await Promise.all([
          mhrApi.getProtocol(protocolId),
          mhrApi.listCriteria(protocolId),
        ]);
        setProtocol(protocolData);
        setCriteria(criteriaData);
      } catch (err: any) {
        console.error('[CriteriaEditor] Error loading data:', err);
        setError(err.response?.data?.error || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [protocolId]);

  // Split criteria by type
  const { inclusionCriteria, exclusionCriteria } = useMemo(() => {
    return {
      inclusionCriteria: criteria.filter(c => c.type === 'inclusion'),
      exclusionCriteria: criteria.filter(c => c.type === 'exclusion'),
    };
  }, [criteria]);

  const handleAddCriterion = (type: CriterionType) => {
    setFormType(type);
    setEditingCriterion(null);
    setShowForm(true);
  };

  const handleEditCriterion = (criterion: Criterion) => {
    setFormType(criterion.type);
    setEditingCriterion(criterion);
    setShowForm(true);
  };

  const handleDeleteCriterion = (criterion: Criterion) => {
    setDeleteModal({ open: true, criterion });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.criterion) return;

    setDeleting(true);
    try {
      await mhrApi.deleteCriterion(protocolId!, deleteModal.criterion.id);
      setCriteria(prev => prev.filter(c => c.id !== deleteModal.criterion!.id));
      setSuccess('Criterion deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete criterion');
    } finally {
      setDeleting(false);
      setDeleteModal({ open: false, criterion: null });
    }
  };

  const handleSubmitCriterion = async (data: CriterionFormData) => {
    setSaving(true);
    try {
      if (editingCriterion) {
        // Update existing
        const updated = await mhrApi.updateCriterion(protocolId!, editingCriterion.id, data);
        setCriteria(prev => prev.map(c => (c.id === updated.id ? updated : c)));
        setSuccess('Criterion updated successfully');
      } else {
        // Create new
        const created = await mhrApi.createCriterion(protocolId!, data);
        setCriteria(prev => [...prev, created]);
        setSuccess('Criterion added successfully');
      }
      setShowForm(false);
      setEditingCriterion(null);
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to save criterion');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSearchTerms = async (
    text: string,
    definition: StructuredDefinition
  ): Promise<string[]> => {
    const response = await mhrApi.generateSearchTerms(protocolId!, {
      criterion_text: text,
      structured_definition: definition,
    });
    return response.search_terms;
  };

  const handleBatchGenerateSearchTerms = () => {
    setShowBatchGenerateModal(true);
  };

  const handleConfirmBatchGenerate = async () => {
    setShowBatchGenerateModal(false);
    setGeneratingAll(true);
    try {
      const result = await mhrApi.batchGenerateSearchTerms(protocolId!);

      // Refresh criteria to get updated search terms
      const updatedCriteria = await mhrApi.listCriteria(protocolId!);
      setCriteria(updatedCriteria);

      setSuccess(`Generated search terms for ${result.updated_count} criteria`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate search terms');
    } finally {
      setGeneratingAll(false);
    }
  };

  // Load templates when template picker opens
  const loadTemplates = useCallback(async () => {
    if (templates) return; // Already loaded

    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const data = await mhrApi.getCriteriaTemplates();
      setTemplates(data);
    } catch (err: any) {
      console.error('[CriteriaEditor] Error loading templates:', err);
      setTemplatesError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, [templates]);

  const handleOpenTemplatePicker = (type: CriterionType) => {
    setTemplatePickerType(type);
    setShowTemplatePicker(true);
    loadTemplates();
  };

  const handleSelectTemplate = (template: CriteriaTemplate, category: CriterionCategory) => {
    // Close the template picker
    setShowTemplatePicker(false);

    // Determine the next criterion number
    const typeCriteria = templatePickerType === 'inclusion' ? inclusionCriteria : exclusionCriteria;
    const prefix = templatePickerType === 'inclusion' ? 'I' : 'E';
    const nextNumber = `${prefix}${typeCriteria.length + 1}`;

    // Set the template data to pre-fill the form
    setTemplateFormData({
      type: templatePickerType,
      criterion_number: nextNumber,
      category: category,
      criterion_text: template.criterion_text,
      structured_definition: template.structured_definition,
      search_terms: [],
      is_critical: false,
      requires_manual_review: false,
    });

    // Open the form with the pre-filled data
    setFormType(templatePickerType);
    setEditingCriterion(null);
    setShowForm(true);
  };

  // Drag-and-drop handlers
  const handleDragStart = (index: number, type: CriterionType) => (e: React.DragEvent) => {
    setDraggedIndex(index);
    setDragType(type);
    e.dataTransfer.effectAllowed = 'move';
    // Add data to support drag operation
    e.dataTransfer.setData('text/plain', `${type}-${index}`);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragType(null);
  };

  const handleDragOver = (index: number, type: CriterionType) => (e: React.DragEvent) => {
    e.preventDefault();
    // Only allow dropping within the same type (inclusion or exclusion)
    if (dragType !== type) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (dropIndex: number, type: CriterionType) => async (e: React.DragEvent) => {
    e.preventDefault();

    // Only allow dropping within the same type
    if (dragType !== type || draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragType(null);
      return;
    }

    // Get the criteria list for this type
    const typeCriteria = type === 'inclusion' ? inclusionCriteria : exclusionCriteria;

    // Create a new order
    const newOrder = [...typeCriteria];
    const [moved] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, moved);

    // Optimistically update UI
    const otherCriteria = criteria.filter(c => c.type !== type);
    setCriteria([...otherCriteria, ...newOrder]);

    // Reset drag state
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragType(null);

    // Call API to persist the new order
    setIsReordering(true);
    try {
      // Generate new criterion numbers based on the order
      const ordering = newOrder.map((c, idx) => ({
        id: c.id,
        criterion_number: type === 'inclusion' ? `I${idx + 1}` : `E${idx + 1}`,
      }));

      await mhrApi.reorderCriteria(protocolId!, ordering);

      // Refresh from server to ensure consistency
      const updatedCriteria = await mhrApi.listCriteria(protocolId!);
      setCriteria(updatedCriteria);

      setSuccess('Criteria order updated');
    } catch (err: any) {
      // Revert on error
      console.error('[CriteriaEditor] Error reordering criteria:', err);
      setError(err.response?.data?.error || 'Failed to reorder criteria');

      // Reload original order from server
      try {
        const originalCriteria = await mhrApi.listCriteria(protocolId!);
        setCriteria(originalCriteria);
      } catch {
        // If reload fails, keep the optimistic update
      }
    } finally {
      setIsReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/mhr' },
    ...(protocol
      ? [{ label: protocol.protocol_number, href: `/mhr/protocols/${protocolId}` }]
      : []),
    { label: 'Criteria' },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/mhr/protocols/${protocolId}`}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Criteria Editor</h1>
            {protocol && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-600 dark:text-gray-400">{protocol.protocol_number}</span>
                <ProtocolStatusBadge status={protocol.status} />
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleBatchGenerateSearchTerms}
          disabled={generatingAll || criteria.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          {generatingAll ? (
            <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Sparkles size={20} />
          )}
          Generate All Search Terms
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
            &times;
          </button>
        </div>
      )}

      {/* Template picker modal */}
      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={handleSelectTemplate}
        templates={templates}
        loading={templatesLoading}
        error={templatesError}
        criterionType={templatePickerType}
      />

      {/* Criterion form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {editingCriterion ? 'Edit Criterion' : templateFormData ? 'Add Criterion from Template' : 'Add Criterion'}
            </h2>
            <CriterionForm
              criterion={editingCriterion}
              type={formType}
              protocolId={protocolId!}
              onSubmit={handleSubmitCriterion}
              onCancel={() => {
                setShowForm(false);
                setEditingCriterion(null);
                setTemplateFormData(null);
              }}
              onGenerateSearchTerms={handleGenerateSearchTerms}
              isLoading={saving}
              initialData={templateFormData}
            />
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        title="Delete Criterion"
        message={
          deleteModal.criterion ? (
            <div className="space-y-2">
              <p>Are you sure you want to delete this criterion?</p>
              <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {deleteModal.criterion.criterion_number}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {deleteModal.criterion.criterion_text}
                </p>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
            </div>
          ) : (
            'Are you sure you want to delete this criterion?'
          )
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModal({ open: false, criterion: null })}
        isLoading={deleting}
      />

      {/* Batch generate confirmation modal */}
      <ConfirmationModal
        isOpen={showBatchGenerateModal}
        title="Generate Search Terms"
        message={
          <div className="space-y-2">
            <p>Generate AI search terms for all {criteria.length} criteria?</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This will use AI to suggest search terms for each criterion. The process may take a moment.
            </p>
          </div>
        }
        confirmLabel="Generate"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmBatchGenerate}
        onCancel={() => setShowBatchGenerateModal(false)}
      />

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inclusion criteria */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Inclusion Criteria</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">({inclusionCriteria.length})</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenTemplatePicker('inclusion')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                <FileText size={16} />
                Use Template
              </button>
              <button
                onClick={() => handleAddCriterion('inclusion')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>

          {inclusionCriteria.length === 0 ? (
            <div className="bg-green-50/50 dark:bg-green-900/20 border border-green-100 dark:border-green-900 rounded-lg p-6 text-center">
              <CheckCircle className="w-8 h-8 text-green-300 mx-auto mb-2" />
              <p className="text-green-700 dark:text-green-300">No inclusion criteria defined yet</p>
              <div className="mt-3 flex items-center justify-center gap-3">
                <button
                  onClick={() => handleOpenTemplatePicker('inclusion')}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                >
                  Use a template
                </button>
                <span className="text-gray-400 dark:text-gray-500">or</span>
                <button
                  onClick={() => handleAddCriterion('inclusion')}
                  className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 underline"
                >
                  Add from scratch
                </button>
              </div>
            </div>
          ) : (
            <div className={`space-y-2 ${isReordering ? 'pointer-events-none opacity-75' : ''}`}>
              {inclusionCriteria.map((criterion, index) => (
                <CriterionCard
                  key={criterion.id}
                  criterion={criterion}
                  categoryLabel={CATEGORY_LABELS_MAP[criterion.category]}
                  onEdit={() => handleEditCriterion(criterion)}
                  onDelete={() => handleDeleteCriterion(criterion)}
                  isDraggable={inclusionCriteria.length > 1}
                  isDragging={draggedIndex === index && dragType === 'inclusion'}
                  isDragOver={dragOverIndex === index && dragType === 'inclusion'}
                  onDragStart={handleDragStart(index, 'inclusion')}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver(index, 'inclusion')}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(index, 'inclusion')}
                />
              ))}
            </div>
          )}
        </div>

        {/* Exclusion criteria */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Exclusion Criteria</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">({exclusionCriteria.length})</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenTemplatePicker('exclusion')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                <FileText size={16} />
                Use Template
              </button>
              <button
                onClick={() => handleAddCriterion('exclusion')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>

          {exclusionCriteria.length === 0 ? (
            <div className="bg-red-50/50 dark:bg-red-900/20 border border-red-100 dark:border-red-900 rounded-lg p-6 text-center">
              <XCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
              <p className="text-red-700 dark:text-red-300">No exclusion criteria defined yet</p>
              <div className="mt-3 flex items-center justify-center gap-3">
                <button
                  onClick={() => handleOpenTemplatePicker('exclusion')}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                >
                  Use a template
                </button>
                <span className="text-gray-400 dark:text-gray-500">or</span>
                <button
                  onClick={() => handleAddCriterion('exclusion')}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
                >
                  Add from scratch
                </button>
              </div>
            </div>
          ) : (
            <div className={`space-y-2 ${isReordering ? 'pointer-events-none opacity-75' : ''}`}>
              {exclusionCriteria.map((criterion, index) => (
                <CriterionCard
                  key={criterion.id}
                  criterion={criterion}
                  categoryLabel={CATEGORY_LABELS_MAP[criterion.category]}
                  onEdit={() => handleEditCriterion(criterion)}
                  onDelete={() => handleDeleteCriterion(criterion)}
                  isDraggable={exclusionCriteria.length > 1}
                  isDragging={draggedIndex === index && dragType === 'exclusion'}
                  isDragOver={dragOverIndex === index && dragType === 'exclusion'}
                  onDragStart={handleDragStart(index, 'exclusion')}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver(index, 'exclusion')}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(index, 'exclusion')}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Help section */}
      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
        <h3 className="font-medium text-primary-900 dark:text-primary-300 mb-2">Tips for Defining Criteria</h3>
        <ul className="text-sm text-primary-700 dark:text-primary-400 space-y-1">
          <li>
            <strong>Inclusion criteria:</strong> Conditions patients must meet to be eligible for the study.
          </li>
          <li>
            <strong>Exclusion criteria:</strong> Conditions that would disqualify a patient from the study.
          </li>
          <li>
            <strong>Search terms:</strong> Help the AI find relevant information in medical records. Use the
            "Generate with AI" button for automatic suggestions.
          </li>
          <li>
            <strong>Critical criteria:</strong> Mark criteria that are immediate disqualifiers if met (e.g.,
            pregnancy in most studies).
          </li>
          <li>
            <strong>Reordering:</strong> Drag criteria using the grip handle to reorder them. The order is saved
            automatically.
          </li>
        </ul>
      </div>

      {/* Success Toast */}
      <Toast message={success} type="success" onClose={() => setSuccess(null)} />
    </div>
  );
}
